import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { anchorBlockDomId, parseReportAnchor } from "../../Utils/reportAnchor";

interface AnchorSection {
  number: string | number;
  displayNumber?: string;
}

interface Options {
  // The manifest's sections, once loaded: the anchor is only followed against them.
  sections: AnchorSection[] | undefined;
  openSection: (sectionId: string, behavior?: ScrollBehavior) => void;
  // Scrolls to the block, returning false while it is not on the page yet.
  scrollToBlock: (blockId: string) => boolean;
}

/**
 * Follows the report anchor in the URL hash (see reportAnchor.ts): opens the
 * section and, for a block, scrolls to it as soon as the section has loaded and
 * the block is on the page. The report only renders the open section, so a block
 * in another one has to wait for its section to arrive.
 *
 * Each navigation is followed once, keyed on `location.key`: the manifest is
 * refetched while the report is generating, and that must not pull the reader
 * back to the anchor. Clicking the same link again is a new navigation, so it
 * does take them back.
 */
export const useReportAnchorNavigation = ({ sections, openSection, scrollToBlock }: Options) => {
  const location = useLocation();
  const followedKey = useRef<string | null>(null);
  const pendingBlock = useRef<string | null>(null);

  useEffect(() => {
    if (!sections?.length || followedKey.current === location.key) return;
    followedKey.current = location.key;
    pendingBlock.current = null;

    const anchor = parseReportAnchor(location.hash);
    if (!anchor) return;
    const section = sections.find((s) => String(s.number) === anchor.sectionNumber);
    if (!section) return;

    // A section link glides up to the section's top; a block link jumps there
    // and glides down to the block instead.
    openSection(`section-${anchor.sectionNumber}`, anchor.blockSuffix ? "auto" : "smooth");
    if (anchor.blockSuffix) {
      pendingBlock.current = anchorBlockDomId(anchor.blockSuffix, section.displayNumber ?? String(section.number));
    }
  }, [sections, location.key, location.hash, openSection]);

  // No deps on purpose: the block shows up on whichever render lands its section's
  // content, and a ref lookup per render is all it costs.
  useEffect(() => {
    if (pendingBlock.current && scrollToBlock(pendingBlock.current)) pendingBlock.current = null;
  });
};
